import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, roleHome } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle } from "lucide-react";
import SavvyLogo from "@/components/SavvyLogo";

const DEMO = [
  { role: "Platform Owner", email: "owner@constructos.com", password: "Owner@123" },
  { role: "Company Admin", email: "admin@acmebuilders.com", password: "Admin@123" },
  { role: "Customer", email: "customer@example.com", password: "Customer@123" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user && user.role) navigate(roleHome(user.role), { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user?.name || "user"}`);
      navigate(roleHome(res.user?.role), { replace: true });
    } else {
      setError(res.error);
    }
  };

  const applyDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-stone-50">
      {/* Left: image */}
      <div className="hidden lg:block relative">
        <img
          alt="construction"
          src="https://images.pexels.com/photos/17815725/pexels-photo-17815725.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-950/55" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <SavvyLogo variant="dark" size={44} />
          </Link>
          <div>
            <div className="overline text-brand-300 mb-3">Transparent construction</div>
            <h2 className="font-display text-4xl md:text-[2.75rem] font-bold leading-[1.05] tracking-[-0.02em] max-w-md">
              Every buyer deserves to{" "}
              <span className="font-serif-accent text-brand-300">see</span>{" "}
              the next brick coming.
            </h2>
            <div className="mt-6 border-l-2 border-brand-500 pl-4 max-w-md">
              <div className="text-sm text-stone-200 leading-relaxed">
                Savvy replaces fragmented status calls with a structured,
                tenant-aware portal for developers and customers.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10" data-testid="mobile-brand">
            <SavvyLogo variant="light" size={42} />
          </Link>

          <div className="overline text-brand-600 mb-3">Ops console</div>
          <h1 className="font-display text-5xl font-bold tracking-[-0.02em] leading-none text-stone-950">
            Sign <span className="font-serif-accent text-brand-600">in</span>.
          </h1>
          <p className="text-stone-600 mt-3 text-base">
            Multi-tenant access for owners, companies, and customers.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label className="overline text-stone-500 block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full border border-stone-300 bg-white px-4 py-3 text-base focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="overline text-stone-500 block mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full border border-stone-300 bg-white px-4 py-3 text-base focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 focus:outline-none"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 border border-red-400 bg-red-50 text-red-800 p-3 text-sm"
              >
                <AlertTriangle size={16} className="mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-brand-500 hover:bg-brand-600 disabled:bg-stone-300 text-white font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 transition-colors"
            >
              {busy ? "Authenticating…" : "Enter console"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-10 border-t border-stone-200 pt-6">
            <div className="overline text-stone-500 mb-3">Demo accounts</div>
            <div className="grid grid-cols-1 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => applyDemo(d)}
                  data-testid={`demo-${d.role.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left border border-stone-200 hover:border-brand-500 hover:bg-brand-50 p-3 transition-colors"
                >
                  <div className="text-xs uppercase tracking-wider font-bold text-stone-500">
                    {d.role}
                  </div>
                  <div className="font-mono text-sm text-stone-950 mt-0.5">
                    {d.email}
                  </div>
                  <div className="font-mono text-xs text-stone-500">pw: {d.password}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center gap-2" data-testid="login-powered-by">
            <div className="text-[10px] tracking-[0.25em] text-stone-400 uppercase">
              Powered by
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_construct-hub-357/artifacts/aps478ky_logo.png"
              alt="SavvySpark Solutions Private Limited"
              className="h-12 w-auto logo-blend-light"
              data-testid="login-savvyspark-logo"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
